import { create } from 'zustand';
import { combine } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';

// Initial state for the product filter store
const initialProductFilterState = {
    // Product Filter Mode State
    mode: 'create',

    // Product Filter State
    restrictToEligibleProducts: true,
    eligibleProductRules: '',
    conditionType: 'allConditions',
    allProducts: [],
    originalFilters: [],
    filters: [],
    // Grouped rules mode: the builder's groups, and the ruleGroups saved with the program as of the last search or load
    ruleGroups: [],
    ruleGroupsPayload: null,
    // Required chains no product matches, as of the last search or load
    emptyRequiredChains: [],
    // Products must match and the All/Any Conditions rules as of the last search or load; a save waits for a search when either changes
    searchedConditionType: null,
    searchedFilters: [],

    // Pagination State
    snapshotId: null,
    totalProductCount: 0,
    currentPage: 1,
    productsPerPage: 10,

    // Loading State
    isProductsLoading: false,

    // Error State
    errors: {},
};

// Create the product filter store with combine middleware for better type inference
const useProductFilterStoreBase = create(
    combine(initialProductFilterState, (set) => ({
        // Actions for Product Filter Mode
        setMode: (mode) => set({ mode }),

        // Actions for Product Filter
        setRestrictToEligibleProducts: (restrictToEligibleProducts) => set({ restrictToEligibleProducts }),
        setEligibleProductRules: (eligibleProductRules) => set({ eligibleProductRules }),
        setConditionType: (conditionType) => set({ conditionType }),
        setAllProducts: (allProducts) => set({ allProducts }),
        setFilters: (filters) => set({ filters }),
        setOriginalFilters: (originalFilters) => set({ originalFilters }),
        setRuleGroups: (ruleGroups) => set({ ruleGroups }),
        setRuleGroupsPayload: (ruleGroupsPayload) => set({ ruleGroupsPayload }),
        setEmptyRequiredChains: (emptyRequiredChains) => set({ emptyRequiredChains }),
        setSearchedConditionType: (searchedConditionType) => set({ searchedConditionType }),
        setSearchedFilters: (searchedFilters) => set({ searchedFilters }),

        // Actions for Pagination
        setSnapshotId: (snapshotId) => set({ snapshotId }),
        setTotalProductCount: (totalProductCount) => set({ totalProductCount }),
        setCurrentPage: (currentPage) => set({ currentPage }),
        setProductsPerPage: (productsPerPage) => set({ productsPerPage }),

        // Actions for Loading State
        setIsProductsLoading: (isProductsLoading) => set({ isProductsLoading }),

        // Actions for Error State
        setErrors: (errors) => set({ errors }),

        // Reset product filter form
        resetProductFilter: () => {
            set({
                ...initialProductFilterState,
            });
        },
    }))
);

// Auto-generated selectors function
const createSelectors = (_store) => {
    let store = _store;
    store.use = {};

    for (let k of Object.keys(store.getState())) {
        store.use[k] = () => store((s) => s[k]);
    }

    return store;
};

// Create the store with auto-generated selectors
export const useProductFilterStore = createSelectors(useProductFilterStoreBase);

/**
 * Selector hooks for optimized component rendering
 * Each hook uses useShallow to prevent unnecessary re-renders
 */

// Selector for product filter actions
export const useProductFilterActions = () =>
    useProductFilterStore(
        useShallow((state) => ({
            setMode: state.setMode,
            setRestrictToEligibleProducts: state.setRestrictToEligibleProducts,
            setEligibleProductRules: state.setEligibleProductRules,
            setConditionType: state.setConditionType,
            setAllProducts: state.setAllProducts,
            setFilters: state.setFilters,
            setOriginalFilters: state.setOriginalFilters,
            setRuleGroups: state.setRuleGroups,
            setRuleGroupsPayload: state.setRuleGroupsPayload,
            setEmptyRequiredChains: state.setEmptyRequiredChains,
            setSearchedConditionType: state.setSearchedConditionType,
            setSearchedFilters: state.setSearchedFilters,
            setSnapshotId: state.setSnapshotId,
            setTotalProductCount: state.setTotalProductCount,
            setCurrentPage: state.setCurrentPage,
            setProductsPerPage: state.setProductsPerPage,
            setIsProductsLoading: state.setIsProductsLoading,
            setErrors: state.setErrors,
            resetProductFilter: state.resetProductFilter,
        }))
    );

// Selector for multi-product filter state
export const useMultiProductFilter = () =>
    useProductFilterStore(
        useShallow((state) => ({
            mode: state.mode,
            restrictToEligibleProducts: state.restrictToEligibleProducts,
            eligibleProductRules: state.eligibleProductRules,
            conditionType: state.conditionType,
            filters: state.filters,
            ruleGroups: state.ruleGroups,
            emptyRequiredChains: state.emptyRequiredChains,
            allProducts: state.allProducts,
            snapshotId: state.snapshotId,
            totalProductCount: state.totalProductCount,
            currentPage: state.currentPage,
            productsPerPage: state.productsPerPage,
            isProductsLoading: state.isProductsLoading,
            errors: state.errors,
        }))
    );
